import { v4 as uuidV4 } from 'uuid';

import { extractBlocks, hasContent, hasHighlights, textToDraftJs } from 'my-core/editor-utils';
import { cloneSkillAssociations, contentSkillTagParams } from 'my-core/mastery-taxonomy-utils';
import { coachRoleForCourse, hasAnyRoles, instructorRoleForCourse } from 'my-core/user-utils';

import { faCheck, faCheckToSlot, faPencil, faRotateRight, faTimes } from '@fortawesome/pro-regular-svg-icons';

// import examIcon from 'images/icons/quiz_green.svg';
import examIcon from 'images/icons/mock-exam.svg';
import quizIcon from 'images/icons/quiz.svg';

export { default as checkSolution, computeScore } from './quiz-question-check-answer';

export const SUBMISSION_STATUS = {
  ANSWERED: 'answered',
  CORRECT: 'correct',
  INCORRECT: 'incorrect',
  MARK_SELF: 'mark_self',
  SKIPPED: 'skipped',
  UNANSWERED: 'unanswered',
  UNSEEN: 'unseen',
};
export const SUBMISSION_STATUS_CONFIG = {
  [SUBMISSION_STATUS.ANSWERED]: { color: 'primary', icon: faPencil, label: 'Answered' },
  [SUBMISSION_STATUS.CORRECT]: { color: 'success', icon: faCheck, label: 'Correct' },
  [SUBMISSION_STATUS.INCORRECT]: { color: 'error', icon: faTimes, label: 'Incorrect' },
  [SUBMISSION_STATUS.MARK_SELF]: { color: 'skyblue', icon: faCheckToSlot, label: 'Unchecked' },
  [SUBMISSION_STATUS.SKIPPED]: { color: 'warning', icon: faRotateRight, label: 'Skipped' },
  [SUBMISSION_STATUS.UNANSWERED]: { color: 'skyblue', icon: faPencil, label: 'Unanswered' },
  [SUBMISSION_STATUS.UNSEEN]: { icon: faPencil, label: 'Unanswered' },
};

export const SUBMISSION_BREAKDOWN_STATUS_KEYS = [
  SUBMISSION_STATUS.CORRECT,
  SUBMISSION_STATUS.INCORRECT,
  SUBMISSION_STATUS.SKIPPED,
];
const SUBMISSION_BREAKDOWN_TOOLTIP_LABELS = {
  [SUBMISSION_STATUS.CORRECT]: 'Correct Answers',
  [SUBMISSION_STATUS.INCORRECT]: 'Wrong Answers',
  [SUBMISSION_STATUS.SKIPPED]: 'Skipped Questions',
};
export const SUBMISSION_BREAKDOWN_BAR_CONFIG = Object.fromEntries(
  SUBMISSION_BREAKDOWN_STATUS_KEYS.map(key => [
    key,
    {
      color: SUBMISSION_STATUS_CONFIG[key].color,
      label: SUBMISSION_STATUS_CONFIG[key].label,
      tooltipLabel: SUBMISSION_BREAKDOWN_TOOLTIP_LABELS[key],
    },
  ]),
);

export function getStatus(submission, question, isCheckedOverride = false) {
  if (isCheckedOverride || isSubmissionChecked(submission)) {
    if (
      submission?.response == null ||
      (question.type === 'MultiPartQuestion' &&
        (question.sub_questions || []).every(q => submission?.response?.[q.id] == null))
    ) {
      return SUBMISSION_STATUS.SKIPPED;
    }
    if (!submission.result?.total && isMarkYourself(question)) return SUBMISSION_STATUS.MARK_SELF;
    if (isResultCorrect(submission.result)) return SUBMISSION_STATUS.CORRECT;
    return SUBMISSION_STATUS.INCORRECT;
  }
  // if (isMarkYourself(question)) return SUBMISSION_STATUS.MARK_SELF;
  if (!submission?.first_seen_ts) return SUBMISSION_STATUS.UNSEEN;
  if (!isQuestionAttempted(question, submission)) return SUBMISSION_STATUS.UNANSWERED;
  return SUBMISSION_STATUS.ANSWERED;
}

export function getMultiPartStatuses(submission, question, isCheckedOverride = false) {
  if (!question?.sub_questions?.length) return [];
  return question.sub_questions.map(sq => getMultiPartStatus(submission, sq, isCheckedOverride));
}
export function getMultiPartStatus(submission, subQuestion, isCheckedOverride = false) {
  const sqResponse = submission?.response?.[subQuestion.id];
  const sqResult = submission?.result?.breakdown?.[subQuestion.id];
  if (isCheckedOverride || isSubmissionChecked(submission) || sqResult?.total) {
    if (sqResponse == null) return SUBMISSION_STATUS.SKIPPED;
    if (!sqResult?.total && isMarkYourself(subQuestion)) return SUBMISSION_STATUS.MARK_SELF;
    if (isResultCorrect(sqResult)) return SUBMISSION_STATUS.CORRECT;
    return SUBMISSION_STATUS.INCORRECT;
  }
  // if (isMarkYourself(sq)) return SUBMISSION_STATUS.MARK_SELF;
  if (!submission?.meta?.[subQuestion.id]?.first_seen_ts) return SUBMISSION_STATUS.UNSEEN;
  if (submission?.response?.[subQuestion.id] === undefined) return SUBMISSION_STATUS.UNANSWERED;
  return SUBMISSION_STATUS.ANSWERED;
}

export function isSubmissionChecked(submission) {
  return submission?.result?.total !== undefined;
}

// `points / total` rather than the stored `score`, which is rounded to two places.
export function isResultCorrect(result) {
  return result?.total > 0 && result.points >= result.total;
}

export function isMarkYourself(question) {
  return (
    question?.type === 'MarkYourself' ||
    (question?.type === 'MultiPartQuestion' && (question.sub_questions || []).every(q => q.type === 'MarkYourself'))
  );
}

export function isQuestionAttempted(question, submission) {
  if (!question) return false;
  // if (isMarkYourself(question)) return true;
  if (question.type === 'MultiPartQuestion') {
    return (question.sub_questions || []).every(
      sq =>
        // sq.type === 'MarkYourself' ||
        submission?.response?.[sq.id] !== undefined,
    );
  }
  return submission?.response !== undefined;
}

export function hasSolution(question) {
  if (!question?.solution) return false;
  if (Array.isArray(question.solution)) return question.solution.some(hasContent);
  return hasContent(question.solution);
}

// Whether there is anything for a "show solution" control to uncover. A MultiPartQuestion carries
// its written solution on the parent, but solution highlights are authored per part.
export function hasRevealableSolution(question, activeSubQuestion) {
  return (
    hasSolution(question) ||
    !!question?.video ||
    hasHighlights(question?.content_solution_highlights) ||
    hasHighlights(activeSubQuestion?.content_solution_highlights)
  );
}

// Staff who are allowed to open a question's worked solution without answering it.
export function canViewInstructionMode(user, { courseSlug, onlineCourse } = {}) {
  if (hasAnyRoles(user, 'admin')) return true;
  if (onlineCourse?.instructor_id && onlineCourse.instructor_id === user?.id) return true;
  return hasAnyRoles(user, [instructorRoleForCourse(courseSlug), coachRoleForCourse(courseSlug)].filter(Boolean));
}

// if a 'cloned' activity, will include all fetched attributes
// that we don't need to send
export function quizToParams({
  base_type,
  course_usage_stats,
  created_at,
  editor_data_fetched,
  elements,
  questions_count,
  relationships,
  updated_at,
  user,
  user_id,
  ...quiz
}) {
  const params = {
    ...quiz,
    elements_attributes: elements?.map(({ clientId, created_at, quiz_id, updated_at, ...qe }) => ({
      ...qe,
      type: `QuizElement::${qe.type}`,
    })),
  };
  delete params.elements;
  return params;
}

export function quizQuestionToParams({
  base_type,
  content_preview,
  content_skill_tags,
  course_usage_stats,
  created_at,
  editor,
  editor_data_fetched,
  has_hint,
  has_solution,
  has_video,
  linked_oc_section_ids,
  linked_sections,
  qa_at,
  qa_by_id,
  quiz_usage_stats,
  relationships,
  submission_stats,
  updated_at,
  user,
  user_id,
  video,
  ...question
}) {
  return {
    ...question,
    video_id: video && video.id, // allow to be 'null' to remove
    linked_section_ids: linked_sections?.map(s => s.id),
    ...contentSkillTagParams(content_skill_tags),
  };
}

export function cloneQuizQuestion(question, userId) {
  return {
    ...question,
    id: undefined,
    description: question.description || '',
    clientId: uuidV4(),
    user_id: userId,
    quiz_usage_stats: { count: 0, recent: [] },
    synced_from_id: question.id,
    synced_from_type: question.id && 'QuizQuestion',
    synced_at: new Date(),
    content_skill_tags: cloneSkillAssociations(question.content_skill_tags),
  };
}

export function convertToNewQuestionType(question, newType) {
  if (question.type === newType) return null;
  const oldType = question.type;
  const updates = { type: newType };
  if (newType === 'MultiPartQuestion' && QUESTION_TYPES_CONFIG[oldType]?.content_fields.some(f => question[f])) {
    updates.sub_questions = [
      {
        id: uuidV4(),
        type: oldType,
        content: question.content,
        ...QUESTION_TYPES_CONFIG[oldType].content_fields.reduce((mem, f) => {
          mem[f] = question[f];
          return mem;
        }, {}),
      },
    ];
  }
  if (oldType === 'MultiPartQuestion') {
    const subQ = question.sub_questions?.find(sq => sq.type === newType);
    if (subQ) {
      QUESTION_TYPES_CONFIG[newType].content_fields.forEach(f => {
        updates[f] = subQ[f];
      });
    }
  }
  if (oldType === 'ShortAnswer' && newType === 'MultipleShortAnswers') {
    updates.inputs = [
      {
        id: uuidV4(),
        label: '',
        answer: question.answer,
        answer_config: question.answer_config,
      },
    ];
  }
  if (oldType === 'MultipleShortAnswers' && newType === 'ShortAnswer') {
    const input = question.inputs?.[0];
    if (input) {
      updates.answer = input.answer;
      updates.answer_config = input.answer_config;
    }
  }
  if (newType === 'FillInTheBlank' && !hasContent(question.fitb_content)) {
    updates.fitb_content = question.content;
  }
  const defaultFields = questionTypeDefaultFields(newType);
  Object.keys(defaultFields).forEach(k => {
    if (!updates[k] && !question[k]) updates[k] = defaultFields[k];
  });
  return updates;
}

export function questionTypeDefaultFields(type) {
  switch (type) {
    case 'MultipleChoice':
      return {
        options: [
          { id: uuidV4(), content: textToDraftJs('A)'), is_correct: true },
          { id: uuidV4(), content: textToDraftJs('B)') },
          { id: uuidV4(), content: textToDraftJs('C)') },
          { id: uuidV4(), content: textToDraftJs('D)') },
        ],
      };
    case 'MultipleShortAnswers':
      return {
        inputs: [
          { id: uuidV4(), label: 'A', answer: '' },
          { id: uuidV4(), label: 'B', answer: '' },
        ],
      };
    case 'TableInputs':
      return {
        input_matrix: [
          [
            { type: 'label', value: '' },
            { type: 'label', value: '' },
          ],
          [
            { type: 'input', value: '' },
            { type: 'input', value: '' },
          ],
        ],
      };
    case 'AccountingJournalEntry':
      return {
        transactions: [
          { id: uuidV4(), account: '', amount: 0 },
          { id: uuidV4(), account: '', amount: 0 },
        ],
      };
  }
  return {};
}

export function courseLessonToQuizQuestion(lesson, defaults = {}) {
  const clientId = uuidV4();
  const question = { clientId, type: 'MarkYourself', base_type: 'QuizQuestion' };
  if (!lesson) return question;

  question.description = lesson.title;
  question.video_elements = lesson.elements;
  question.video = lesson.video;
  if (lesson.id) {
    question.synced_from_type = 'CourseLesson';
    question.synced_from_id = lesson.id;
    question.synced_at = new Date();
  }
  question.tag_list = lesson.tag_list;
  question.user_id = lesson.user_id;

  const lessonContent = lesson.content;
  if (lessonContent) {
    question.content = extractBlocks(lessonContent, b => !b.data.solution);
    question.solution = extractBlocks(lessonContent, b => b.data.solution);
  }
  return {
    ...question,
    ...defaults,
  };
}

export function getQuizIcon(quiz) {
  return quiz.type === 'practice_exam' ? examIcon : quizIcon;
}

export const QUIZ_TYPES_CONFIG = {
  regular: {
    label: 'Standard Quiz',
    description: '',
  },
  practice_exam: {
    label: 'Mock Exam',
    description: '',
  },
  // quiz: {
  //   label: 'Quiz',
  //   description: ''
  // },
};

export const NON_QUESTION_ELEMENT_TYPES_CONFIG = {
  InstructionPage: { label: 'Instruction Page', iconChar: 'I', color: 'cyan' },
  SectionBreak: { label: 'Section Break', iconChar: '§', color: 'purple' },
};

export const QUESTION_TYPES_CONFIG = {
  MixMatch: {
    label: 'Mix & Match',
    description: 'Mix and match terms and definitions',
    abbrv: 'M&M',
    color: '',
    content_fields: ['pairings'],
  },
  MarkYourself: {
    label: 'Mark Yourself',
    description: 'No answer - students check solution',
    abbrv: 'MY',
    color: '',
    content_fields: ['answer_key'],
  },
  MultipleChoice: {
    label: 'Multiple Choice',
    description: '',
    abbrv: 'MC',
    color: '',
    content_fields: ['options', 'allow_multiple'],
  },
  ShortAnswer: {
    label: 'Short Answer',
    description: '',
    abbrv: 'SA',
    color: '',
    content_fields: ['answer', 'answer_config'],
  },
  FillInTheBlank: {
    label: 'Fill In The Blank',
    description: '',
    abbrv: 'FB',
    color: '',
    content_fields: ['fitb_content'],
  },
  MultipleShortAnswers: {
    label: 'Multiple Short Answers',
    description: '',
    abbrv: 'MSA',
    color: '',
    content_fields: ['inputs'],
  },
  TableInputs: {
    label: 'Table Inputs',
    description: '',
    abbrv: 'TI',
    color: '',
    content_fields: ['input_matrix'],
  },
  AccountingJournalEntry: {
    label: 'Journal Entry (Accounting)',
    description: '',
    abbrv: 'JE',
    color: '',
    content_fields: ['transactions', 'journal_entry_account_titles'],
  },
  MultiPartQuestion: {
    label: 'Multi-part Question',
    description: '',
    abbrv: 'MPQ',
    color: '',
    content_fields: ['sub_questions'],
  },
};

export const QUESTION_CONTENT_FIELDS = [
  ...Object.values(QUESTION_TYPES_CONFIG).flatMap(q => q.content_fields),
  'content_highlights',
];

export const ACCOUNTING_JOURNAL_DEFAULT_ACCOUNT_TITLES = [
  'Accounts payable',
  'Accounts receivable',
  'Accumulated amortization',
  'Accumulated amortization - copyrights',
  'Accumulated amortization - patents',
  'Accumulated amortization - software',
  'Accumulated amortization - trademarks',
  'Accumulated depreciation',
  'Accumulated depreciation - buildings',
  'Accumulated depreciation - equipment',
  'Accumulated depreciation - furniture',
  'Accumulated depreciation - leasehold improvements',
  'Accumulated depreciation - machinery',
  'Advertising expense',
  'Allowance for doubtful accounts',
  'Amortization expense',
  'Bad debt expense',
  'Bank service charges',
  'Bonds payable',
  'Cash',
  'Common shares',
  'Contributed capital',
  'Contributed surplus',
  'Copyrights',
  'Cost of goods sold',
  'CPP payable',
  'Credit card discounts',
  'Debt investments',
  'Deferred income tax asset',
  'Deferred income tax liability',
  'Depreciation expense',
  'Discount on bonds payable',
  'Dividend revenue',
  'Dividends declared',
  'Dividends payable',
  'Dividends payable - common shares',
  'Dividends payable - preferred shares',
  'Dividends receivable',
  'EI payable',
  'Employee benefit expense',
  'Fair value adjustment – non-trading',
  'Fair value adjustment – trading',
  'Freight-in',
  'Freight-out',
  'Gain on early redemption',
  'Gain on sale',
  'Gain on sale of buildings',
  'Gain on sale of debt investments',
  'Gain on sale of equipment',
  'Gain on sale of investments',
  'Gain on sale of land',
  'Gain on sale of patents',
  'Gain on sale of trademarks',
  'Goodwill',
  'Group insurance premiums payable',
  'GST payable',
  'HST payable',
  'Impairment loss',
  'Income summary',
  'Income tax expense',
  'Income tax payable',
  'Income tax withhheld payable',
  'Insurance expense',
  'Interest expense',
  'Interest payable',
  'Interest receivable',
  'Interest revenue',
  'Land',
  'Long-term investments',
  'Long-term notes payable',
  'Loss on early redemption',
  'Loss on sale',
  'Loss on sale of buildings',
  'Loss on sale of debt investments',
  'Loss on sale of equipment',
  'Loss on sale of investments',
  'Loss on sale of land',
  'Loss on sale of patents',
  'Loss on sale of trademarks',
  'Merchandise inventory',
  'No entry',
  'Notes payable',
  'Notes receivable',
  'Patents',
  'Pension plan contributions payable',
  'Preferred shares',
  'Premium on bonds payable',
  'Prepaid advertising',
  'Prepaid expenses',
  'Prepaid insurance',
  'Prepaid rent',
  'PST payable',
  'Purchase discounts',
  'Purchase returns and allowances',
  'Purchases',
  'Realized gain on trading investments',
  'Realized loss on trading investments',
  'Rent expense',
  'Rent receivable',
  'Rent revenue',
  'Retained earnings',
  'Revenue from stock investments',
  'Salaries and wages expense',
  'Salaries and wages payable',
  'Sales discounts',
  'Sales returns and allowances',
  'Sales revenue',
  'Sales tax payable',
  'Selling and administrative expenses',
  'Service revenue',
  'Short-term investments',
  'Short-term notes payable',
  'Stock dividends to be issued',
  'Stock investments',
  'Supplies',
  'Supplies expense',
  'Trademarks',
  'Trading investments',
  'Unearned revenue',
  'Union dues payable',
  'Unrealized gain – income',
  'Unrealized gain on trading investments',
  'Unrealized gain or loss – equity',
  'Unrealized loss – income',
  'Unrealized loss on trading investments',
  'Utilities expense',
  'Warranty expense',
  'Warranty liability',
  'Buildings',
  'Equipment',
  'Furniture and fixtures',
  'Machinery',
  'Vehicles',
  'Petty cash',
  'Repair expense',
  'FICA social security tax payable',
  'FICA medicare tax payable',
  'Federal income tax payable',
  'State income tax payable',
  '401(k) plan payable',
  'Federal unemployment tax payable',
  'State unemployment tax payable',
  'Charitable contributions payable',
];
